import React, { useState } from 'react';
import { useQuery } from 'react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import { Lock } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { authAPI } from '../../services/api';
import PasswordField from '../../components/Auth/PasswordField';

const emptyForm = { currentPassword: '', newPassword: '', confirmPassword: '' };

const Profile = () => {
  const { data, isLoading, refetch } = useQuery('profile-me', authAPI.getMe);
  const user = data?.data?.user;

  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const mismatch = form.confirmPassword.length > 0 && form.newPassword !== form.confirmPassword;
  const needsCurrent = user?.hasPassword !== false;

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPassword !== form.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const response = await authAPI.changePassword(form.currentPassword, form.newPassword);
      toast.success(response.data.message || 'Mot de passe modifié');
      setForm(emptyForm);
      refetch();
    } catch (err) {
      setError(err.response?.data?.message || 'Impossible de modifier le mot de passe. Réessayez.');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography variant="h4" gutterBottom>
        Mon Profil
      </Typography>

      {user && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar src={user.photo || undefined} sx={{ width: 56, height: 56 }}>
              {user.name?.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" noWrap>
                {user.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {user.email}
              </Typography>
            </Box>
            <Chip
              label={user.role === 'admin' ? 'Administrateur' : 'Élève'}
              color={user.role === 'admin' ? 'primary' : 'default'}
              size="small"
              sx={{ ml: 'auto' }}
            />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Lock color="primary" />
            <Typography variant="h6">
              {needsCurrent ? 'Changer mon mot de passe' : 'Créer un mot de passe'}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {!needsCurrent && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Votre compte a été créé via Telegram. Définissez un mot de passe pour pouvoir aussi vous connecter sur le site.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            {needsCurrent && (
              <PasswordField
                margin="normal"
                required
                label="Mot de passe actuel"
                autoComplete="current-password"
                value={form.currentPassword}
                onChange={handleChange('currentPassword')}
                disabled={saving}
              />
            )}
            <PasswordField
              margin="normal"
              required
              label="Nouveau mot de passe"
              autoComplete="new-password"
              inputProps={{ minLength: 6 }}
              value={form.newPassword}
              onChange={handleChange('newPassword')}
              disabled={saving}
              helperText="Minimum 6 caractères"
            />
            <PasswordField
              margin="normal"
              required
              label="Confirmer le nouveau mot de passe"
              autoComplete="new-password"
              value={form.confirmPassword}
              onChange={handleChange('confirmPassword')}
              disabled={saving}
              error={mismatch}
              helperText={mismatch ? 'Les mots de passe ne correspondent pas' : ' '}
            />
            <Button
              type="submit"
              variant="contained"
              sx={{ mt: 1 }}
              disabled={saving || mismatch}
            >
              {saving ? <CircularProgress size={24} /> : 'Enregistrer'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Profile;
